<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { t } from 'svelte-i18n';
	import { clickOutside } from '@wiseed/wikit';
	import { ButtonSVG } from '@wiseed/wikit';
	import { isSidebarOpen } from '../stores/sidebar';
	import { signInUpModalStore } from '../stores/signInUpModal';
	import MainNav from './nav/mainNav/MainNav.svelte';
	import UserNav from './nav/userNav/UserNav.svelte';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import { viewport } from '@wiseed/wikit';
	import { ButtonBoxPrimary } from '@wiseed/wikit';
	import { bannersHeight } from '@app/stores/banners';
	import { ButtonTextPrimary } from '@wiseed/wikit';
	import { headerHeight } from '@app/stores/header';
	import { ButtonBoxTextColor } from '@wiseed/wikit';
	import UserNavInstallAppCTA from '@app/components/nav/NavInstallAppCTA.svelte';
	import { logError } from '@app/utils/log';

	export let toggleSidebarRefs = [];

	let header: HTMLElement;
	let startX = 0; // Position de départ
	let swipeThreshold = 100; // 100 px de swipe pour afficher le menu
	/* Feature detection */
	let passiveIfSupported = false;

	const toggleNavDrawer = () => {
		$isSidebarOpen = !$isSidebarOpen;
		header.style.transform = '';
	};

	const handleMobileNavTouchstart = (evt) => {
		// Récupère les "touches" effectuées
		let touches = evt.changedTouches[0];
		startX = touches.pageX;
	};

	const handleMobileNavTouchmove = (evt) => {
		try {
			window.addEventListener(
				'test',
				null,
				Object.defineProperty({}, 'passive', {
					get() {
						passiveIfSupported = { passive: true };
					}
				})
			);
		} catch (err) {
			logError('Unexpected error', err);
		}
		// Limite les effets de bord avec le tactile...
		!passiveIfSupported && evt.preventDefault();
		evt.stopPropagation();
	};

	const handleMobileNavTouchend = (evt) => {
		let orientation;
		let touches = evt.changedTouches[0];
		let between = touches.pageX - startX;

		// Détection de la direction
		if (between > 0) {
			orientation = 'ltr';
		} else {
			orientation = 'rtl';
		}

		// Créé l'effet pour le menu slide (compatible partout)
		if (Math.abs(between) >= swipeThreshold && orientation === 'ltr' && $isSidebarOpen) {
			toggleNavDrawer();
		}
	};

	const removeSwipeNavListeners = () => {
		if (!browser || !header) return;
		header.removeEventListener('touchstart', handleMobileNavTouchstart);
		header.removeEventListener('touchmove', handleMobileNavTouchmove);
		header.removeEventListener('touchend', handleMobileNavTouchend);
	};

	const setSwipeNavListeners = () => {
		if (!browser || !header) return;

		// Au premier point de contact
		header.addEventListener('touchstart', handleMobileNavTouchstart, false);

		// Quand les points de contact sont en mouvement
		header.addEventListener('touchmove', handleMobileNavTouchmove, passiveIfSupported);

		// Quand le contact s'arrête
		header.addEventListener('touchend', handleMobileNavTouchend, false);
	};

	onMount(async () => {
		headerHeight.set(header.offsetHeight);
	});
	$: if (!$viewport.isDesktop) {
		setSwipeNavListeners();
	} else {
		removeSwipeNavListeners();
	}
	onDestroy(() => {
		headerHeight.set(0);
	});
</script>

<header
	bind:this={header}
	use:clickOutside={{ action: () => ($isSidebarOpen = false), excludedRefs: toggleSidebarRefs }}
	class:open={$isSidebarOpen}
	class={!$isSidebarOpen ? 'bg-white' : ''}
	style="top:{$viewport.isDesktop ? `${$bannersHeight}px` : '0'}"
>
	<div class="container h-full lg:h-auto">
		<div
			class="lg:flex lg:items-center {$page.data.user
				? 'lg:gap-4 2xl:gap-5'
				: 'lg:gap-6 2xl:gap-7'} h-full overflow-auto lg:h-auto lg:overflow-visible"
		>
			<ButtonSVG
				props={{
					href: '/',
					classNames: 'block text-primary flex-shrink-0 mb-4 lg:mb-0',
					iconClassNames: '!w-[108px] !h-[38px] lg:!w-[137px] lg:!h-[49px]',
					name: 'logo_wiseed_baseline'
				}}
				on:click={() => ($isSidebarOpen = false)}
			/>
			{#if $page.data.user}
				<UserNav />
			{/if}
			<div
				class="flex items-start pt-4 lg:pt-0 lg:flex-1 {$page.data.user
					? 'lg:justify-center'
					: 'lg:justify-start'}"
			>
				<MainNav />
			</div>
			<div class="flex flex-col items-start lg:flex-row lg:items-center gap-4">
				{#if !$page.data.user}
					<ButtonTextPrimary
						props={{ label: $t('nav.header.login'), classNames: 'font-bold' }}
						on:click={() => signInUpModalStore.open()}
					/>
					<ButtonBoxPrimary
						props={{ label: $t('nav.header.signup') }}
						on:click={() => {
							signInUpModalStore.open({ panel: 'register' });
						}}
					/>

					<UserNavInstallAppCTA />
				{:else}
					<div class="lg:hidden">
						<UserNavInstallAppCTA />
					</div>
				{/if}
			</div>
			<div class="lg:hidden fixed bottom-4 right-4">
				<ButtonBoxTextColor
					props={{ iconLeft: 'cross', roundedFull: true }}
					on:click={() => {
						$isSidebarOpen = false;
					}}
				/>
			</div>
		</div>
	</div>
</header>

<style lang="postcss">
	header {
		@apply transform transition-transform translate-x-full overflow-auto fixed top-0 right-0 w-3/4 h-full z-nav pt-4;
		@apply lg:sticky lg:w-full lg:translate-x-0 lg:pb-4 lg:h-auto lg:overflow-visible;

		&.open {
			@apply -translate-x-0 duration-500;
		}
	}
</style>
